export function renderHome(): string {
  return /* html */`
<div class="page-wrapper home-page">

  <!-- Hero -->
  <section class="home-hero">
    <div class="hero-inner">
      <div class="hero-eyebrow anim-fade-up">
        <span class="badge badge-teal">Santa Cruz, CA</span>
        <span class="badge badge-dim">est. 2024</span>
      </div>

      <h1 class="hero-title anim-fade-up" style="animation-delay:0.1s">
        <span class="gradient-text">Xaebia</span><br/>
        <span class="hero-sub">builds software that feels alive.</span>
      </h1>

      <p class="hero-mission anim-fade-up" style="animation-delay:0.2s">
        Future-facing AI tools, web apps, open-source systems, and experimental
        interfaces — built for people who want technology to feel alive again.
      </p>

      <div class="hero-actions anim-fade-up" style="animation-delay:0.3s">
        <a href="#apps" class="btn btn-primary">Explore Apps</a>
        <a href="#projects" class="btn btn-secondary">GitHub Projects</a>
        <a href="#store" class="btn btn-ghost">Visit Store</a>
      </div>
    </div>
  </section>

  <!-- 3D play hint -->
  <div class="play-hint anim-fade-up" style="animation-delay:0.5s">
    <span class="play-hint-icon">◈</span>
    <span>Click the glowing portals in the world to navigate • Switch to Play mode above</span>
  </div>

  <!-- Feature strip -->
  <section class="home-features page-section">
    <span class="section-label">What we build</span>
    <div class="features-grid stagger">
      ${[
        { icon: '🤖', color: 'teal',   title: 'AI Applications', body: 'Native AI tools, assistants, and integrations that work the way developers actually work.' },
        { icon: '🌐', color: 'purple', title: 'Web Apps',         body: 'Browser-native applications with no-compromise UX, offline support, and real performance.' },
        { icon: '📱', color: 'green',  title: 'Mobile Apps',      body: 'iOS and Android apps built with privacy-first, local-first architecture.' },
        { icon: '⚙️', color: 'amber',  title: 'Linux / CLI Tools',body: 'Terminal tools and desktop apps for Linux, Mac, and Windows that respect your workflow.' },
        { icon: '🔬', color: 'pink',   title: 'Experimental',     body: "Speculative interfaces, creative systems, and prototype technology we're not afraid to ship." },
        { icon: '🔓', color: 'teal',   title: 'Open Source',      body: "Most of what we build is open. GitHub is our portfolio, not just our backup drive." },
      ].map(f => `
        <div class="feature-card card">
          <div class="feature-icon feature-icon--${f.color}">${f.icon}</div>
          <h3 class="feature-title">${f.title}</h3>
          <p class="feature-body">${f.body}</p>
        </div>
      `).join('')}
    </div>
  </section>

  <!-- Featured apps preview -->
  <section class="home-apps page-section">
    <div class="section-header">
      <div>
        <span class="section-label">Featured Apps</span>
        <h2 class="section-title">Apps you can use right now</h2>
      </div>
      <a href="#apps" class="btn btn-ghost">All apps →</a>
    </div>
    <div id="home-apps-grid" class="grid-3 stagger">
      <!-- Populated by JS -->
    </div>
  </section>

  <!-- Open source -->
  <section class="home-oss page-section">
    <div class="oss-card">
      <div class="oss-left">
        <span class="section-label">Open Source</span>
        <h2 class="section-title">Everything on GitHub</h2>
        <p class="section-body">
          Our work is public by default. Browse the full list of repositories at
          <strong>github.com/xirtus</strong>, fork what's useful, and open issues
          for anything broken.
        </p>
        <div class="oss-actions">
          <a href="#projects" class="btn btn-primary">Explore Projects</a>
          <a href="https://github.com/xirtus" class="btn btn-ghost" target="_blank" rel="noopener">
            github.com/xirtus ↗
          </a>
        </div>
      </div>
      <div class="oss-stars anim-float" aria-hidden="true">
        <canvas id="mini-constellation" width="260" height="200"></canvas>
      </div>
    </div>
  </section>

  <!-- Contact strip -->
  <section class="home-contact page-section">
    <div class="contact-strip">
      <div>
        <h2 class="section-title" style="font-size:var(--text-2xl)">Work with Xaebia</h2>
        <p style="color:var(--text-2)">Collaboration, consulting, or just saying hi.</p>
      </div>
      <a href="#contact" class="btn btn-primary">Get in touch →</a>
    </div>
  </section>

</div>
`;
}

export function initHome(): void {
  import('../data/apps.json').then(({ default: apps }) => {
    const featured = (apps as any[]).filter(a => a.featured).slice(0, 3);
    const grid = document.getElementById('home-apps-grid');
    if (!grid) return;
    grid.innerHTML = featured.map(app => `
      <a href="#apps" class="app-mini-card card" style="--app-color:${app.color}">
        <div class="app-mini-icon">${app.icon}</div>
        <div class="app-mini-info">
          <div class="app-mini-name">${app.name}</div>
          <div class="app-mini-tag">${app.tagline}</div>
          <div class="app-mini-badges">
            ${app.platforms.map((p: string) => `<span class="badge badge-dim">${p}</span>`).join('')}
          </div>
        </div>
        <span class="badge badge-${pricingColor(app.pricing)}">${app.pricing}</span>
      </a>
    `).join('');
  });

  drawMiniConstellation();
}

function pricingColor(p: string): string {
  if (p === 'Open Source') return 'green';
  if (p === 'Free') return 'teal';
  if (p === 'Experimental') return 'pink';
  return 'amber';
}

function drawMiniConstellation(): void {
  const canvas = document.getElementById('mini-constellation') as HTMLCanvasElement;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const w = canvas.width, h = canvas.height;
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < 18; i++) {
    pts.push({ x: Math.random() * w, y: Math.random() * h });
  }

  ctx.clearRect(0, 0, w, h);

  // Lines
  ctx.strokeStyle = 'rgba(0, 255, 240, 0.15)';
  ctx.lineWidth = 1;
  pts.forEach((a, i) => {
    pts.forEach((b, j) => {
      if (i >= j) return;
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (d < 80) {
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    });
  });

  // Stars
  pts.forEach((p, i) => {
    const r = i === 0 ? 4 : 2.5;
    const color = i % 3 === 0 ? '#00fff0' : i % 3 === 1 ? '#8b5cf6' : '#10b981';
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    ctx.fill();
  });
}
